import { useState } from 'react';
import { Plus, Pencil, Trash2, FolderPlus, MapPin, Building, ChevronRight, ChevronDown, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  useDeleteLocationsByIdMutation,
  useGetLocationsQuery,
  usePatchLocationsByIdMutation,
  usePostLocationsByParentIdSubMutation,
  usePostLocationsMutation,
} from '@/services';
import { toast } from '@/hooks/use-toast';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from '@/components/ui/skeleton';

export const Route = createFileRoute('/_authenticated/(user-dashboard)/dashboard/_dashboard/locations')({
  component: RouteComponent,
});

function LocationsTableSkeleton() {
  return (
    <div className="w-full">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex w-full items-center space-x-4 py-4">
          <Skeleton className="h-6 w-1/4" />
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-8 w-24 ml-auto" />
        </div>
      ))}
    </div>
  );
}

function RouteComponent() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());
  const [locationToDelete, setLocationToDelete] = useState<any>(null);

  const { data: locations, isLoading, refetch } = useGetLocationsQuery({ includeSubLocations: 'true' });
  const [createLocation, { isLoading: isCreating }] = usePostLocationsMutation();
  const [updateLocation, { isLoading: isUpdating }] = usePatchLocationsByIdMutation();
  const [deleteLocation, { isLoading: isDeleting }] = useDeleteLocationsByIdMutation();
  const [createSubLocation, { isLoading: isCreatingSubLocation }] = usePostLocationsByParentIdSubMutation();

  // Combined loading state for create operations
  const isSaving = isCreating || isUpdating || isCreatingSubLocation;

  const resetForm = () => {
    setForm({ name: '', description: '' });
    setSelectedLocation(null);
    setIsEditMode(false);
  };

  const handleCreate = async () => {
    try {
      if (selectedLocation) {
        // Creating a sub-location
        await createSubLocation({
          parentId: selectedLocation.id,
          body: form,
        }).unwrap();
        // Auto-expand the parent location to show the new sub-location
        setExpandedLocations((prev) => new Set(prev).add(selectedLocation.id));
      } else {
        await createLocation({ body: form }).unwrap();
      }
      toast({
        title: 'Success',
        description: 'Location created successfully',
      });
      setIsModalOpen(false);
      resetForm();
      // Explicitly refetch to ensure the list is updated
      refetch();
    } catch (_error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to create location',
      });
    }
  };

  const handleUpdate = async () => {
    try {
      await updateLocation({
        id: selectedLocation.id,
        body: form,
      }).unwrap();
      toast({
        title: 'Success',
        description: 'Location updated successfully',
      });
      setIsModalOpen(false);
      resetForm();
      // Explicitly refetch to ensure the list is updated
      refetch();
    } catch (_error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to update location',
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteLocation({ id }).unwrap();
      toast({
        title: 'Success',
        description: 'Location deleted successfully',
      });
      setLocationToDelete(null);
      // Explicitly refetch to ensure the list is updated
      refetch();
    } catch (_error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to delete location',
      });
    }
  };

  const confirmDelete = (location: any) => {
    setLocationToDelete(location);
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Locations data has been refreshed',
    });
  };

  const toggleLocationExpansion = (locationId: string) => {
    setExpandedLocations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  const renderLocationRow = (location: any, isSubLocation = false) => {
    const hasSubLocations = location.sub_locations && location.sub_locations.length > 0;
    const isExpanded = expandedLocations.has(location.id);

    return (
      <>
        <TableRow key={location.id} className={isSubLocation ? 'bg-muted/30' : ''}>
          <TableCell>
            <div className={`flex items-center ${isSubLocation ? 'ml-6' : ''}`}>
              {hasSubLocations && !isSubLocation && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-0 w-6 h-6 mr-2"
                  onClick={() => toggleLocationExpansion(location.id)}
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
              )}
              {isSubLocation && <div className="w-6 h-6 mr-2" />}
              <div className="flex items-center">
                {isSubLocation ? (
                  <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                ) : (
                  <Building className="h-4 w-4 mr-2 text-primary" />
                )}
                <span>{location.name}</span>
                {hasSubLocations && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {location.sub_locations.length}
                  </Badge>
                )}
              </div>
            </div>
          </TableCell>
          <TableCell>{location.description}</TableCell>
          <TableCell>
            <div className="flex space-x-2">
              {!isSubLocation && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedLocation(location);
                    setIsEditMode(false);
                    setIsModalOpen(true);
                  }}
                  title="Add sub-location"
                >
                  <FolderPlus className="h-4 w-4" />
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedLocation(location);
                  setForm({
                    name: location.name,
                    description: location.description || '',
                  });
                  setIsEditMode(true);
                  setIsModalOpen(true);
                }}
                title="Edit location"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => confirmDelete(location)}
                title="Delete location"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
        {hasSubLocations &&
          isExpanded &&
          !isSubLocation &&
          location.sub_locations.map((subLocation: any) => renderLocationRow(subLocation, true))}
      </>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Locations</CardTitle>
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Location
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {isEditMode ? 'Edit Location' : selectedLocation ? 'Add Sub-location' : 'Add Location'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  placeholder="Description"
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                />
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button onClick={isEditMode ? handleUpdate : handleCreate} disabled={isSaving || !form.name.trim()}>
                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isEditMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LocationsTableSkeleton />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[200px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{locations?.data.map((location) => renderLocationRow(location))}</TableBody>
          </Table>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={locationToDelete !== null} onOpenChange={() => setLocationToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{locationToDelete?.name}"?
              {locationToDelete?.sub_locations?.length > 0 && (
                <span className="block mt-2 text-destructive font-medium">
                  This location has {locationToDelete.sub_locations.length} sub-location(s) that will also be deleted.
                </span>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => locationToDelete && handleDelete(locationToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
